import { TOWN_FLOOR_SWATCHES, TOWN_INK, TOWN_WALL_SWATCHES, type TownFacing } from "@town/contracts";
import { drawSpeakingCharacter, getCharacterSheet, speakingFrameAt, walkFrameAt } from "../pixel/characterSprites";
import { furnitureSprites } from "../pixel/furnitureSprites";
import { createPixelCanvas } from "../pixel/paint";
import { LANDING_LOOKS } from "./landingLooks";
import {
  canHear,
  facingFor,
  findPath,
  FRONT_DOOR,
  LANDING_COLUMNS,
  LANDING_COWORKERS,
  LANDING_FURNITURE,
  LANDING_ROWS,
  LANDING_SPAWN,
  LANDING_WALL_ROWS,
  MEETING_DOORWAY,
  MEETING_ROOM,
  routePosition,
  stepPlayer,
  TALK_RADIUS,
  zoneAt,
  type Coworker,
  type Point,
} from "./landingWorld";

export type PlaygroundStatus = Readonly<{
  kind: "walk" | "talk" | "muffled" | "meeting" | "door" | "entering";
  /** Who the player can hear, for "talk". */
  name: string | null;
  /** The player stands behind the headline card, which should step aside. */
  covered: boolean;
}>;

const TILE = 32;
const WORLD_WIDTH = LANDING_COLUMNS * TILE;
const WORLD_HEIGHT = LANDING_ROWS * TILE;
const PAGE = "#141722";
const IRIS_MS = 720;
const SPEECH_GREEN = "#57d38c";
const FONT = '"Fredoka Variable", "Segoe UI", sans-serif';

type Placed = { key: number; draw: (context: CanvasRenderingContext2D) => void };
type Label = { x: number; y: number; text: string; you: boolean; speaking: boolean; bubble: string | null; bubbleSince: number; muffled: boolean };

/** Deterministic texel noise so the carpet and grass fleck the same way every load. */
function noise(x: number, y: number): number {
  const value = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return value - Math.floor(value);
}

function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/**
 * A small walkable office for the landing page. It owns its render loop,
 * keyboard and pointer input; React hands it a canvas through a callback ref
 * and reads a coarse status for the caption under the scene.
 */
export class LandingPlayground {
  readonly #onEnter: () => void;
  readonly #listeners = new Set<() => void>();
  #reducedMotion: boolean;
  #night: number;

  #canvas: HTMLCanvasElement | null = null;
  #context: CanvasRenderingContext2D | null = null;
  #floor: HTMLCanvasElement | null = null;
  #lightMask: HTMLCanvasElement | null = null;
  #glow: HTMLCanvasElement | null = null;
  #resizeObserver: ResizeObserver | null = null;
  #intersection: IntersectionObserver | null = null;
  #frame = 0;
  #lastFrame = 0;
  #inView = false;

  #time = 0;
  #player: { x: number; y: number; facing: TownFacing; moving: boolean } = { x: LANDING_SPAWN.x, y: LANDING_SPAWN.y, facing: "north", moving: false };
  #look = 0;
  #hopSince = -10;
  #keys = new Set<string>();
  #path: Point[] = [];
  #stalled = 0;
  #marker: (Point & { since: number }) | null = null;
  #pointer: { id: number; x: number; y: number; mouse: boolean; moved: boolean } | null = null;
  #camera = { x: LANDING_SPAWN.x * TILE, y: LANDING_SPAWN.y * TILE, ready: false };
  #roomFocus = 0;
  #heard = new Map<string, number>();
  #enteringSince: number | null = null;
  #entered = false;
  #status: PlaygroundStatus = { kind: "walk", name: null, covered: false };
  #cover: HTMLElement | null = null;
  #covered = false;
  /** The door sign in canvas pixels; tapping it walks to the door. */
  #signRect = { x: 0, y: 0, width: 0, height: 0 };

  constructor(options: { onEnter: () => void; night: number; reducedMotion: boolean }) {
    this.#onEnter = options.onEnter;
    this.#night = options.night;
    this.#reducedMotion = options.reducedMotion;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  getStatus = (): PlaygroundStatus => this.#status;

  /** The card laid over the scene; the runtime reports when the player walks behind it. */
  setCover(element: HTMLElement | null): void {
    this.#cover = element;
  }

  setLook(look: number): void {
    if (look === this.#look) return;
    this.#look = look;
    this.#hopSince = this.#time;
  }

  setNight(night: number): void {
    if (Math.abs(night - this.#night) < 0.01) return;
    this.#night = night;
    this.#floor = null;
    this.#lightMask = null;
    this.#glow = null;
  }

  attach(canvas: HTMLCanvasElement): void {
    this.#canvas = canvas;
    this.#context = canvas.getContext("2d");
    void furnitureSprites.load();
    canvas.addEventListener("pointerdown", this.#pointerDown);
    canvas.addEventListener("pointermove", this.#pointerMove);
    canvas.addEventListener("pointerup", this.#pointerUp);
    canvas.addEventListener("pointercancel", this.#pointerCancel);
    window.addEventListener("keydown", this.#keyDown);
    window.addEventListener("keyup", this.#keyUp);
    window.addEventListener("blur", this.#clearKeys);
    document.addEventListener("visibilitychange", this.#visibility);
    this.#resizeObserver = new ResizeObserver(this.#resize);
    this.#resizeObserver.observe(canvas);
    this.#intersection = new IntersectionObserver(([entry]) => {
      this.#inView = Boolean(entry?.isIntersecting);
      this.#visibility();
    });
    this.#intersection.observe(canvas);
    this.#resize();
  }

  detach(): void {
    const canvas = this.#canvas;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#resizeObserver?.disconnect();
    this.#intersection?.disconnect();
    window.removeEventListener("keydown", this.#keyDown);
    window.removeEventListener("keyup", this.#keyUp);
    window.removeEventListener("blur", this.#clearKeys);
    document.removeEventListener("visibilitychange", this.#visibility);
    if (canvas) {
      canvas.removeEventListener("pointerdown", this.#pointerDown);
      canvas.removeEventListener("pointermove", this.#pointerMove);
      canvas.removeEventListener("pointerup", this.#pointerUp);
      canvas.removeEventListener("pointercancel", this.#pointerCancel);
    }
    this.#canvas = null;
    this.#context = null;
  }

  /** Walk to the front door, for the page's own "Enter" buttons when the scene is on screen. */
  walkToDoor(): void {
    this.#path = findPath(this.#player, { x: 33.6, y: (FRONT_DOOR.y0 + FRONT_DOOR.y1) / 2 });
    this.#stalled = 0;
    this.#marker = null;
  }

  // ---------------------------------------------------------------- input

  #visibility = (): void => {
    const running = this.#inView && document.visibilityState === "visible" && this.#canvas;
    if (running && !this.#frame) {
      this.#lastFrame = performance.now();
      this.#frame = requestAnimationFrame(this.#tick);
    } else if (!running && this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }
  };

  #resize = (): void => {
    const canvas = this.#canvas;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(canvas.clientWidth * ratio));
    canvas.height = Math.max(1, Math.round(canvas.clientHeight * ratio));
    if (!this.#frame) this.#draw();
  };

  #keyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || !this.#inView || this.#enteringSince !== null) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
    const arrows = event.code.startsWith("Arrow");
    // Arrow keys still scroll the page unless the office has focus; WASD walks whenever it is on screen.
    if (arrows ? document.activeElement !== this.#canvas : !/^Key[WASD]$/.test(event.code)) return;
    event.preventDefault();
    this.#keys.add(event.code);
    this.#path = [];
    this.#marker = null;
  };

  #keyUp = (event: KeyboardEvent): void => {
    this.#keys.delete(event.code);
  };

  #clearKeys = (): void => this.#keys.clear();

  #pointerDown = (event: PointerEvent): void => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const mouse = event.pointerType === "mouse";
    this.#pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, mouse, moved: false };
    // Focus so arrow keys walk too, without the keyboard focus ring a click should not show.
    this.#canvas?.focus({ preventScroll: true, focusVisible: false } as FocusOptions);
    if (mouse) {
      this.#canvas?.setPointerCapture(event.pointerId);
      this.#walkTo(event.clientX, event.clientY);
    }
  };

  #pointerMove = (event: PointerEvent): void => {
    const pointer = this.#pointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    if (Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 10) pointer.moved = true;
    if (pointer.mouse && pointer.moved) this.#walkTo(event.clientX, event.clientY);
  };

  #pointerUp = (event: PointerEvent): void => {
    const pointer = this.#pointer;
    this.#pointer = null;
    // Touch walks on a tap; a swipe is left to the page so it still scrolls.
    if (pointer && !pointer.mouse && !pointer.moved && pointer.id === event.pointerId) this.#walkTo(event.clientX, event.clientY);
  };

  #pointerCancel = (): void => {
    this.#pointer = null;
  };

  #walkTo(clientX: number, clientY: number): void {
    const canvas = this.#canvas;
    if (!canvas || this.#enteringSince !== null) return;
    const rect = canvas.getBoundingClientRect();
    const { scale, originX, originY } = this.#view();
    const ratio = canvas.width / Math.max(1, rect.width);
    const sign = this.#signRect;
    const px = (clientX - rect.left) * ratio;
    const py = (clientY - rect.top) * ratio;
    if (px >= sign.x && px <= sign.x + sign.width && py >= sign.y && py <= sign.y + sign.height) {
      this.walkToDoor();
      return;
    }
    const world = {
      x: ((clientX - rect.left) * ratio - originX) / scale / TILE,
      y: ((clientY - rect.top) * ratio - originY) / scale / TILE,
    };
    this.#path = findPath(this.#player, world);
    this.#stalled = 0;
    const end = this.#path.at(-1);
    this.#marker = end ? { ...end, since: this.#time } : null;
  }

  // ---------------------------------------------------------------- simulation

  #tick = (now: number): void => {
    const seconds = Math.min(0.05, Math.max(0, (now - this.#lastFrame) / 1000));
    this.#lastFrame = now;
    this.#update(seconds);
    this.#draw();
    this.#frame = requestAnimationFrame(this.#tick);
  };

  #coworkerAt(coworker: Coworker): { x: number; y: number; facing: TownFacing; moving: boolean } {
    if (!coworker.route || this.#reducedMotion) return { x: coworker.x, y: coworker.y, facing: coworker.facing, moving: false };
    const { point, direction } = routePosition(coworker.route, this.#time + 0.8);
    const moving = Boolean(direction.x || direction.y);
    return { ...point, facing: moving ? facingFor(direction, coworker.facing) : "south", moving };
  }

  #update(seconds: number): void {
    this.#time += seconds;
    const player = this.#player;
    if (this.#enteringSince === null) {
      const keys = this.#keys;
      const press = (...codes: string[]) => codes.some((code) => keys.has(code)) ? 1 : 0;
      let direction = {
        x: press("KeyD", "ArrowRight") - press("KeyA", "ArrowLeft"),
        y: press("KeyS", "ArrowDown") - press("KeyW", "ArrowUp"),
      };
      const following = !direction.x && !direction.y && this.#path.length > 0;
      if (following) {
        const next = this.#path[0]!;
        if (Math.hypot(next.x - player.x, next.y - player.y) < 0.08) this.#path.shift();
        const waypoint = this.#path[0];
        direction = waypoint ? { x: waypoint.x - player.x, y: waypoint.y - player.y } : { x: 0, y: 0 };
        if (!waypoint) this.#marker = null;
      }
      // Never overshoot the last waypoint on a long frame.
      const remaining = this.#path.length === 1 ? Math.hypot(direction.x, direction.y) : Infinity;
      const next = stepPlayer(player, direction, Math.min(seconds, remaining / 4.2));
      const moved = Math.hypot(next.x - player.x, next.y - player.y);
      player.moving = moved > 0.0005;
      if (player.moving) player.facing = facingFor(direction, player.facing);
      player.x = next.x;
      player.y = next.y;
      if (following) {
        this.#stalled = player.moving ? 0 : this.#stalled + seconds;
        if (this.#stalled > 0.3) { this.#path = []; this.#marker = null; }
      }
      if (zoneAt(player) === "door") this.#enter();
    } else {
      player.moving = false;
      if (!this.#entered && (this.#reducedMotion || performance.now() - this.#enteringSince >= IRIS_MS)) {
        this.#entered = true;
        this.#onEnter();
      }
    }

    const inMeeting = zoneAt(player) === "meeting";
    const focusTarget = inMeeting ? 1 : 0;
    this.#roomFocus += (focusTarget - this.#roomFocus) * (1 - Math.exp(-seconds * 7));

    let nearest: { name: string; distance: number } | null = null;
    for (const coworker of LANDING_COWORKERS) {
      const at = this.#coworkerAt(coworker);
      const hears = canHear(player, { ...at, room: coworker.room });
      if (hears && !this.#heard.has(coworker.name)) this.#heard.set(coworker.name, this.#time);
      if (!hears) this.#heard.delete(coworker.name);
      const distance = Math.hypot(at.x - player.x, at.y - player.y);
      if (hears && !coworker.room && (!nearest || distance < nearest.distance)) nearest = { name: coworker.name, distance };
    }
    const nearMeeting = !inMeeting && player.x < MEETING_ROOM.x1 + 2.5 && player.y < MEETING_ROOM.y1 + 2.5;
    const covered = this.#covered;
    this.#setStatus(
      this.#enteringSince !== null ? { kind: "entering", name: null, covered }
        : inMeeting ? { kind: "meeting", name: null, covered }
          : nearest ? { kind: "talk", name: nearest.name, covered }
            : player.x > 28 && player.y > 8 ? { kind: "door", name: null, covered }
              : nearMeeting ? { kind: "muffled", name: null, covered }
                : { kind: "walk", name: null, covered },
    );
  }

  #enter(): void {
    this.#enteringSince = performance.now();
    this.#path = [];
    this.#keys.clear();
    this.#marker = null;
  }

  #setStatus(status: PlaygroundStatus): void {
    if (status.kind === this.#status.kind && status.name === this.#status.name && status.covered === this.#status.covered) return;
    this.#status = status;
    for (const listener of this.#listeners) listener();
  }

  // ---------------------------------------------------------------- drawing

  /** Device pixels per texel, always whole so every sprite pixel stays square. */
  #scale(): number {
    const canvas = this.#canvas!;
    const narrow = canvas.clientWidth < 700;
    const fit = Math.floor(Math.min(canvas.width / ((narrow ? 11 : 20) * TILE), canvas.height / (9 * TILE)));
    return Math.max(1, fit, Math.ceil(canvas.width / WORLD_WIDTH), Math.ceil(canvas.height / WORLD_HEIGHT));
  }

  #view(): { scale: number; originX: number; originY: number } {
    const canvas = this.#canvas!;
    const scale = this.#scale();
    const viewWidth = canvas.width / scale;
    const viewHeight = canvas.height / scale;
    const clamp = (value: number, view: number, world: number) =>
      view >= world ? world / 2 : Math.min(world - view / 2, Math.max(view / 2, value));
    const x = clamp(this.#camera.x, viewWidth, WORLD_WIDTH);
    const y = clamp(this.#camera.y, viewHeight, WORLD_HEIGHT);
    return { scale, originX: Math.round(canvas.width / 2 - x * scale), originY: Math.round(canvas.height / 2 - y * scale) };
  }

  #draw(): void {
    const canvas = this.#canvas;
    const context = this.#context;
    if (!canvas || !context) return;
    const player = this.#player;
    // On wide screens the headline card covers the left of the scene, so the player sits right of centre.
    const wide = canvas.clientWidth >= 900;
    const viewWidth = canvas.width / this.#scale();
    const target = { x: player.x * TILE - (wide ? viewWidth * 0.17 : 0), y: (player.y + 0.4) * TILE };
    if (!this.#camera.ready) {
      this.#camera = { ...target, ready: true };
    } else {
      const follow = this.#reducedMotion ? 1 : 0.12;
      this.#camera.x += (target.x - this.#camera.x) * follow;
      this.#camera.y += (target.y - this.#camera.y) * follow;
    }
    const { scale, originX, originY } = this.#view();
    const ratio = canvas.width / Math.max(1, canvas.clientWidth);

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.imageSmoothingEnabled = false;
    context.fillStyle = PAGE;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.setTransform(scale, 0, 0, scale, originX, originY);

    context.drawImage(this.#floorLayer(), 0, 0);
    this.#drawDoorLight(context);
    this.#drawMarker(context);

    const labels: Label[] = [];
    const placed: Placed[] = [];
    for (const item of LANDING_FURNITURE) {
      placed.push({
        key: item.y + (item.layer ?? 0) * 0.5,
        draw: (ctx) => {
          const sprite = furnitureSprites.byId(item.id, item.rotation);
          if (sprite) ctx.drawImage(sprite.canvas, Math.round(item.x * TILE - sprite.anchorX), Math.round(item.y * TILE - sprite.anchorY));
        },
      });
    }
    placed.push({ key: MEETING_ROOM.y1, draw: (ctx) => this.#drawGlassFront(ctx) });

    const inMeeting = zoneAt(player) === "meeting";
    for (const coworker of LANDING_COWORKERS) {
      const at = this.#coworkerAt(coworker);
      const heardSince = this.#heard.get(coworker.name);
      const speaking = coworker.room === "meeting" || heardSince !== undefined;
      const nearRoom = coworker.room === "meeting" && !inMeeting
        && Math.hypot(player.x - (MEETING_DOORWAY.x0 + MEETING_DOORWAY.x1) / 2, player.y - MEETING_ROOM.y1) < 4.5;
      const line = heardSince === undefined ? null
        : coworker.lines[Math.floor((this.#time - heardSince) / 3.8) % coworker.lines.length]!;
      const lineSince = heardSince === undefined ? 0 : heardSince + Math.floor((this.#time - heardSince) / 3.8) * 3.8;
      placed.push({ key: at.y, draw: (ctx) => this.#drawPerson(ctx, coworker.avatar, at.x, at.y, at.facing, at.moving, coworker.seated, speaking, 0) });
      labels.push({ x: at.x, y: at.y, text: coworker.name, you: false, speaking: heardSince !== undefined, bubble: line, bubbleSince: lineSince, muffled: nearRoom });
    }
    const hop = this.#reducedMotion ? 0 : Math.max(0, Math.sin(Math.min(1, (this.#time - this.#hopSince) / 0.32) * Math.PI)) * 6;
    placed.push({ key: player.y, draw: (ctx) => this.#drawPerson(ctx, this.#look, player.x, player.y, player.facing, player.moving, false, false, hop) });
    labels.push({ x: player.x, y: player.y - hop / TILE, text: "You", you: true, speaking: this.#heard.size > 0, bubble: null, bubbleSince: 0, muffled: false });

    placed.sort((a, b) => a.key - b.key);
    for (const item of placed) item.draw(context);

    this.#drawNight(context);
    this.#drawRoomFocus(context);

    context.setTransform(1, 0, 0, 1, 0, 0);
    const toScreen = (x: number, y: number) => ({ x: originX + x * TILE * scale, y: originY + y * TILE * scale });
    this.#covered = this.#isCovered(toScreen(player.x, player.y), ratio);
    this.#drawHearingRing(context, toScreen(player.x, player.y), TALK_RADIUS * TILE * scale, ratio);
    for (const label of labels) this.#drawLabel(context, toScreen(label.x, label.y), label, scale, ratio);
    const door = toScreen(FRONT_DOOR.x0 - 0.2, (FRONT_DOOR.y0 + FRONT_DOOR.y1) / 2 - 0.9);
    // Off screen, the sign waits at the nearest edge and points the way.
    this.#drawDoorSign(context, {
      x: Math.min(door.x, canvas.width - 14 * ratio),
      y: Math.min(Math.max(door.y, 56 * ratio), canvas.height - 96 * ratio),
    }, ratio);
    this.#drawIris(context, toScreen(player.x, player.y - 0.5));
  }

  /** Whether the player, or the conversation above their head, sits under the headline card. */
  #isCovered(foot: Point, ratio: number): boolean {
    const cover = this.#cover;
    const canvas = this.#canvas;
    if (!cover || !canvas || cover.offsetParent === null || getComputedStyle(cover).position !== "absolute") return false;
    const card = cover.getBoundingClientRect();
    const frame = canvas.getBoundingClientRect();
    const x = frame.left + foot.x / ratio;
    const y = frame.top + foot.y / ratio;
    const reach = this.#heard.size > 0 || zoneAt(this.#player) === "meeting" ? 260 : 120;
    return x > card.left - 40 && x < card.right + 40 && y - reach < card.bottom && y > card.top;
  }

  #drawPerson(context: CanvasRenderingContext2D, look: number, x: number, y: number, facing: TownFacing, moving: boolean, seated: boolean, speaking: boolean, hop: number): void {
    const sheet = getCharacterSheet(LANDING_LOOKS[look % LANDING_LOOKS.length], look);
    const frame = moving ? walkFrameAt(this.#time, "walk") : 0;
    const sprite = seated ? sheet.seated[facing] : sheet.walk[facing][frame]!;
    const px = Math.round(x * TILE - sprite.anchorX);
    const py = Math.round(y * TILE - sprite.anchorY - hop);
    if (!seated) {
      context.fillStyle = "rgba(20, 22, 34, 0.22)";
      context.beginPath();
      context.ellipse(Math.round(x * TILE), Math.round(y * TILE) - 1, 8, 3, 0, 0, Math.PI * 2);
      context.fill();
    }
    if (speaking && !this.#reducedMotion) drawSpeakingCharacter(context, sprite, px, py, facing, frame, seated, speakingFrameAt(this.#time));
    else context.drawImage(sprite.canvas, px, py);
  }

  #drawGlassFront(context: CanvasRenderingContext2D): void {
    const y = MEETING_ROOM.y1 * TILE;
    const swatch = TOWN_WALL_SWATCHES;
    const pane = (x0: number, x1: number) => {
      context.fillStyle = swatch.pane;
      context.fillRect(x0, y - 22, x1 - x0, 22);
      context.fillStyle = swatch.paneStreak;
      for (let x = x0 + 10; x < x1 - 6; x += 37) context.fillRect(x, y - 19, 3, 12);
      context.fillStyle = swatch.silverShade;
      context.fillRect(x0, y - 24, x1 - x0, 3);
      context.fillRect(x0, y - 1, x1 - x0, 3);
      context.fillStyle = swatch.silver;
      context.fillRect(x0, y - 24, x1 - x0, 1);
      for (let x = x0; x <= x1; x += TILE) {
        context.fillStyle = swatch.post;
        context.fillRect(Math.min(x, x1 - 3), y - 25, 3, 27);
      }
    };
    pane(MEETING_ROOM.x0 * TILE, MEETING_DOORWAY.x0 * TILE);
    pane(MEETING_DOORWAY.x1 * TILE, MEETING_ROOM.x1 * TILE + 4);
  }

  /** Daylight spills in through the open door, and floor arrows run toward it. */
  #drawDoorLight(context: CanvasRenderingContext2D): void {
    const cx = FRONT_DOOR.x1 * TILE;
    const cy = ((FRONT_DOOR.y0 + FRONT_DOOR.y1) / 2) * TILE;
    const pulse = this.#reducedMotion ? 0.5 : 0.5 + Math.sin(this.#time * 2.4) * 0.5;
    const gradient = context.createRadialGradient(cx, cy, 4, cx, cy, TILE * 3.4);
    gradient.addColorStop(0, `rgba(122, 222, 255, ${0.5 + pulse * 0.2})`);
    gradient.addColorStop(1, "rgba(122, 222, 255, 0)");
    context.fillStyle = gradient;
    context.fillRect(cx - TILE * 3.5, cy - TILE * 3.5, TILE * 3.5, TILE * 7);
    for (let index = 0; index < 4; index += 1) {
      const wave = this.#reducedMotion ? 0.6 : (Math.sin(this.#time * 5 - index * 0.9) + 1) / 2;
      const x = Math.round((29.6 + index * 0.85) * TILE);
      const y = Math.round(cy);
      context.fillStyle = `rgba(0, 161, 215, ${0.25 + wave * 0.6})`;
      for (let row = 0; row < 7; row += 1) {
        const offset = 6 - Math.abs(row - 3) * 2;
        context.fillRect(x + offset, y - 14 + row * 4, 6, 4);
      }
    }
  }

  #drawMarker(context: CanvasRenderingContext2D): void {
    const marker = this.#marker;
    if (!marker) return;
    const age = this.#time - marker.since;
    const size = Math.round(6 + (this.#reducedMotion ? 0 : Math.sin(age * 8) * 1.5));
    const x = Math.round(marker.x * TILE);
    const y = Math.round(marker.y * TILE);
    context.fillStyle = "rgba(0, 184, 236, 0.35)";
    context.fillRect(x - size, y - 1, size * 2, 2);
    context.fillRect(x - 1, y - size / 2, 2, size);
    context.strokeStyle = "#00b8ec";
    context.lineWidth = 1;
    context.beginPath();
    context.ellipse(x, y, size + 2, (size + 2) / 2, 0, 0, Math.PI * 2);
    context.stroke();
  }

  #drawRoomFocus(context: CanvasRenderingContext2D): void {
    if (this.#roomFocus < 0.01) return;
    const room = { x0: MEETING_ROOM.x0 * TILE, y0: 0, x1: MEETING_ROOM.x1 * TILE + 4, y1: MEETING_ROOM.y1 * TILE + 2 };
    context.fillStyle = `rgba(10, 12, 24, ${0.5 * this.#roomFocus})`;
    context.fillRect(room.x1, 0, WORLD_WIDTH - room.x1, WORLD_HEIGHT);
    context.fillRect(0, room.y1, room.x1, WORLD_HEIGHT - room.y1);
  }

  /** Night darkens the floor except where lamps, busy screens and the door throw light. */
  #drawNight(context: CanvasRenderingContext2D): void {
    if (this.#night < 0.02) return;
    if (!this.#lightMask || !this.#glow) {
      const mask = createPixelCanvas(WORLD_WIDTH, WORLD_HEIGHT);
      const glow = createPixelCanvas(WORLD_WIDTH, WORLD_HEIGHT);
      const maskContext = mask.getContext("2d")!;
      const glowContext = glow.getContext("2d")!;
      maskContext.fillStyle = `rgba(10, 12, 34, ${0.66 * this.#night})`;
      maskContext.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
      maskContext.globalCompositeOperation = "destination-out";
      const light = (x: number, y: number, radius: number, warm: string) => {
        const cut = maskContext.createRadialGradient(x, y, 0, x, y, radius);
        cut.addColorStop(0, "rgba(0,0,0,0.95)");
        cut.addColorStop(1, "rgba(0,0,0,0)");
        maskContext.fillStyle = cut;
        maskContext.fillRect(x - radius, y - radius, radius * 2, radius * 2);
        const tint = glowContext.createRadialGradient(x, y, 0, x, y, radius);
        tint.addColorStop(0, warm);
        tint.addColorStop(1, "rgba(0,0,0,0)");
        glowContext.fillStyle = tint;
        glowContext.fillRect(x - radius, y - radius, radius * 2, radius * 2);
      };
      const occupiedDesks = LANDING_COWORKERS.filter((coworker) => coworker.seated && !coworker.room);
      for (const item of LANDING_FURNITURE) {
        if (item.light === "lamp") light(item.x * TILE, (item.y - 0.8) * TILE, TILE * 3, `rgba(255, 196, 120, ${0.22 * this.#night})`);
        if (item.light === "screen" && (zoneAt(item) === "meeting" || occupiedDesks.some((coworker) => Math.hypot(coworker.x - item.x, coworker.y - item.y) < 1.2))) {
          light(item.x * TILE, (item.y - 0.3) * TILE, TILE * 2, `rgba(130, 200, 255, ${0.18 * this.#night})`);
        }
      }
      // The meeting room is in use, so its lights are on.
      light(5.5 * TILE, 5 * TILE, TILE * 5, `rgba(255, 214, 150, ${0.12 * this.#night})`);
      light(FRONT_DOOR.x1 * TILE, ((FRONT_DOOR.y0 + FRONT_DOOR.y1) / 2) * TILE, TILE * 4, `rgba(122, 222, 255, ${0.2 * this.#night})`);
      this.#lightMask = mask;
      this.#glow = glow;
    }
    context.drawImage(this.#lightMask, 0, 0);
    context.globalCompositeOperation = "lighter";
    context.drawImage(this.#glow, 0, 0);
    context.globalCompositeOperation = "source-over";
  }

  #drawHearingRing(context: CanvasRenderingContext2D, centre: Point, radius: number, ratio: number): void {
    if (this.#enteringSince !== null || zoneAt(this.#player) === "meeting") return;
    const hearing = this.#heard.size > 0;
    context.save();
    context.strokeStyle = hearing ? "rgba(87, 211, 140, 0.75)" : "rgba(255, 255, 255, 0.22)";
    context.fillStyle = hearing ? "rgba(87, 211, 140, 0.07)" : "rgba(255, 255, 255, 0.03)";
    context.lineWidth = 2 * ratio;
    context.setLineDash([6 * ratio, 6 * ratio]);
    context.lineDashOffset = this.#reducedMotion ? 0 : -this.#time * 14 * ratio;
    context.beginPath();
    context.ellipse(centre.x, centre.y, radius, radius * 0.92, 0, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.restore();
  }

  #drawLabel(context: CanvasRenderingContext2D, foot: Point, label: Label, scale: number, ratio: number): void {
    const head = foot.y - 40 * scale;
    context.font = `600 ${12 * ratio}px ${FONT}`;
    const dot = label.speaking ? 10 * ratio : 0;
    const width = context.measureText(label.text).width + 14 * ratio + dot;
    const height = 18 * ratio;
    const x = Math.round(foot.x - width / 2);
    const y = Math.round(head - height);
    context.fillStyle = label.you ? "#00a1d7" : "rgba(38, 41, 53, 0.88)";
    context.beginPath();
    context.roundRect(x, y, width, height, 6 * ratio);
    context.fill();
    if (label.speaking) {
      context.fillStyle = SPEECH_GREEN;
      context.beginPath();
      context.arc(x + 9 * ratio, y + height / 2, 3 * ratio, 0, Math.PI * 2);
      context.fill();
    }
    context.fillStyle = "#ffffff";
    context.textBaseline = "middle";
    context.fillText(label.text, x + 7 * ratio + dot, y + height / 2 + ratio * 0.5);

    const text = label.bubble ?? (label.muffled ? "· · ·" : null);
    if (!text) return;
    this.#drawBubble(context, foot.x, y - 6 * ratio, text, label.muffled && !label.bubble, label.bubbleSince, ratio);
  }

  #drawBubble(context: CanvasRenderingContext2D, centreX: number, bottom: number, text: string, muffled: boolean, since: number, ratio: number): void {
    context.font = `500 ${13 * ratio}px ${FONT}`;
    const maxWidth = 220 * ratio;
    const lines: string[] = [];
    for (const word of text.split(" ")) {
      const last = lines.at(-1);
      if (last !== undefined && context.measureText(`${last} ${word}`).width <= maxWidth) lines[lines.length - 1] = `${last} ${word}`;
      else lines.push(word);
    }
    const lineHeight = 17 * ratio;
    const width = Math.max(...lines.map((line) => context.measureText(line).width)) + 20 * ratio;
    const height = lines.length * lineHeight + 12 * ratio;
    const age = muffled ? 1 : (this.#time - since) / 0.2;
    const t = this.#reducedMotion ? 1 : Math.min(1, Math.max(0, age));
    const grow = 1 - (1 - t) ** 3;
    context.save();
    context.globalAlpha = muffled ? 0.7 : grow;
    context.translate(centreX, bottom);
    // Bubbles grow out of the speaker, from the tail.
    context.scale(0.9 + 0.1 * grow, 0.9 + 0.1 * grow);
    const x = -width / 2;
    const y = -height - 6 * ratio;
    context.fillStyle = muffled ? "#c3c8d8" : "#fbf8f3";
    context.strokeStyle = TOWN_INK;
    context.lineWidth = 2 * ratio;
    context.beginPath();
    context.roundRect(x, y, width, height, 9 * ratio);
    context.moveTo(-5 * ratio, y + height);
    context.lineTo(0, y + height + 6 * ratio);
    context.lineTo(5 * ratio, y + height);
    context.fill();
    context.stroke();
    context.fillRect(-4 * ratio, y + height - 2 * ratio, 8 * ratio, 3 * ratio);
    context.fillStyle = TOWN_INK;
    context.textBaseline = "middle";
    context.textAlign = "center";
    lines.forEach((line, index) => context.fillText(line, 0, y + 6 * ratio + lineHeight * (index + 0.5)));
    context.restore();
  }

  #drawDoorSign(context: CanvasRenderingContext2D, anchor: Point, ratio: number): void {
    const bob = this.#reducedMotion ? 0 : Math.sin(this.#time * 3) * 3 * ratio;
    context.font = `600 ${13 * ratio}px ${FONT}`;
    const text = "Your office";
    const width = context.measureText(text).width + 34 * ratio;
    const height = 26 * ratio;
    const x = Math.round(anchor.x - width + 10 * ratio);
    const y = Math.round(anchor.y - height + bob);
    this.#signRect = { x, y: y - 6 * ratio, width, height: height + 12 * ratio };
    context.fillStyle = "#0076a8";
    context.beginPath();
    context.roundRect(x, y + 3 * ratio, width, height, 8 * ratio);
    context.fill();
    context.fillStyle = "#00a1d7";
    context.beginPath();
    context.roundRect(x, y, width, height, 8 * ratio);
    context.fill();
    context.fillStyle = "#ffffff";
    context.textBaseline = "middle";
    context.textAlign = "left";
    context.fillText(text, x + 10 * ratio, y + height / 2 + ratio * 0.5);
    const arrowX = x + width - 16 * ratio;
    const arrowY = y + height / 2;
    context.fillRect(arrowX - 4 * ratio, arrowY - ratio, 8 * ratio, 2 * ratio);
    context.beginPath();
    context.moveTo(arrowX + 6 * ratio, arrowY);
    context.lineTo(arrowX + ratio, arrowY - 5 * ratio);
    context.lineTo(arrowX + ratio, arrowY + 5 * ratio);
    context.fill();
  }

  /** The office closes in on the player as they walk through the door, then the sign-in page opens. */
  #drawIris(context: CanvasRenderingContext2D, centre: Point): void {
    if (this.#enteringSince === null || this.#reducedMotion) return;
    const canvas = this.#canvas!;
    const t = Math.min(1, (performance.now() - this.#enteringSince) / IRIS_MS);
    const reach = Math.hypot(Math.max(centre.x, canvas.width - centre.x), Math.max(centre.y, canvas.height - centre.y));
    const radius = reach * (1 - easeInOut(t));
    context.fillStyle = PAGE;
    context.beginPath();
    context.rect(0, 0, canvas.width, canvas.height);
    context.arc(centre.x, centre.y, Math.max(0, radius), 0, Math.PI * 2, true);
    context.fill("evenodd");
  }

  // ---------------------------------------------------------------- static floor and walls

  #floorLayer(): HTMLCanvasElement {
    if (this.#floor) return this.#floor;
    const canvas = createPixelCanvas(WORLD_WIDTH, WORLD_HEIGHT);
    const context = canvas.getContext("2d")!;
    const floors = TOWN_FLOOR_SWATCHES;
    const wall = TOWN_WALL_SWATCHES;
    const rect = (color: string, x: number, y: number, width: number, height: number) => {
      context.fillStyle = color;
      context.fillRect(Math.round(x), Math.round(y), Math.round(width), Math.round(height));
    };
    const pile = (swatch: { base: string; dark: string; light: string }, x0: number, y0: number, x1: number, y1: number) => {
      rect(swatch.base, x0 * TILE, y0 * TILE, (x1 - x0) * TILE, (y1 - y0) * TILE);
      for (let y = y0 * TILE; y < y1 * TILE; y += 2) {
        for (let x = x0 * TILE; x < x1 * TILE; x += 2) {
          const value = noise(x, y);
          if (value < 0.16) rect(swatch.dark, x, y, 2, 2);
          else if (value > 0.9) rect(swatch.light, x, y, 2, 2);
        }
      }
    };
    const planks = (swatch: { boards: readonly [string, string, string]; seam: string }, x0: number, y0: number, x1: number, y1: number) => {
      for (let y = y0 * TILE; y < y1 * TILE; y += 8) {
        const row = Math.floor(y / 8);
        rect(swatch.boards[row % 3]!, x0 * TILE, y, (x1 - x0) * TILE, 8);
        rect(swatch.seam, x0 * TILE, y + 7, (x1 - x0) * TILE, 1);
        for (let x = x0 * TILE + ((row * 23) % 48); x < x1 * TILE; x += 48 + (row % 2) * 16) rect(swatch.seam, x, y, 1, 7);
      }
    };

    pile(floors.carpet.grey, 0, LANDING_WALL_ROWS, LANDING_COLUMNS, LANDING_ROWS);
    pile(floors.carpet.blue, MEETING_ROOM.x0, MEETING_ROOM.y0, MEETING_ROOM.x1, MEETING_ROOM.y1);
    planks(floors.planks.hall, 23.5, LANDING_WALL_ROWS, LANDING_COLUMNS - 1, 8);
    // Courtyard garden under the meeting room.
    const grass = floors.grass.meadow;
    pile({ base: grass.base, dark: grass.blade, light: grass.light }, 0.5, MEETING_ROOM.y1, 10, LANDING_ROWS - 0.5);
    for (let index = 0; index < 60; index += 1) {
      const x = (0.7 + noise(index, 3) * 9) * TILE;
      const y = (MEETING_ROOM.y1 + 0.4 + noise(index, 7) * 6) * TILE;
      rect(grass.flowers[index % grass.flowers.length]!, x, y, 2, 2);
    }
    rect(floors.pavers.path.base, 4.5 * TILE, MEETING_ROOM.y1 * TILE, 2 * TILE, 1.6 * TILE);
    for (let y = MEETING_ROOM.y1 * TILE; y < (MEETING_ROOM.y1 + 1.6) * TILE; y += 8) {
      rect(floors.pavers.path.joint, 4.5 * TILE, y + 7, 2 * TILE, 1);
      for (let x = 4.5 * TILE + (Math.floor(y / 8) % 2) * 8; x < 6.5 * TILE; x += 16) rect(floors.pavers.path.joint, x, y, 1, 7);
    }
    // North wall: slate cap, plaster face with windows, baseboard.
    const night = this.#night >= 0.5;
    rect(wall.cap, 0, 0, WORLD_WIDTH, 10);
    rect(wall.capRim, 0, 10, WORLD_WIDTH, 2);
    rect(wall.plaster, 0, 12, WORLD_WIDTH, 44);
    rect(wall.plasterShade, 0, 48, WORLD_WIDTH, 8);
    rect(wall.baseboard, 0, 56, WORLD_WIDTH, 8);
    for (const column of [3, 7, 15, 19, 29]) {
      const x = column * TILE - 22;
      rect(wall.sill, x - 3, 44, 50, 4);
      rect(night ? wall.navy : wall.glass, x, 16, 44, 28);
      rect(night ? wall.navyLight : wall.glassStreak, x + 6, 19, 4, 20);
      rect(night ? wall.navyLight : wall.glassStreak, x + 13, 19, 2, 14);
      rect(wall.silverShade, x + 21, 16, 2, 28);
    }
    // Room divider: the meeting room's east glass.
    rect(wall.pane, MEETING_ROOM.x1 * TILE - 2, LANDING_WALL_ROWS * TILE, 5, (MEETING_ROOM.y1 - LANDING_WALL_ROWS) * TILE);
    rect(wall.silverShade, MEETING_ROOM.x1 * TILE - 2, LANDING_WALL_ROWS * TILE, 1, (MEETING_ROOM.y1 - LANDING_WALL_ROWS) * TILE);
    rect(wall.silverShade, MEETING_ROOM.x1 * TILE + 2, LANDING_WALL_ROWS * TILE, 1, (MEETING_ROOM.y1 - LANDING_WALL_ROWS) * TILE);
    rect(wall.post, 0.5 * TILE, LANDING_WALL_ROWS * TILE - 24, 6, 24);
    // Side and south walls, seen from above as slate caps.
    const slate = (x: number, y: number, width: number, height: number) => {
      rect(wall.slate, x, y, width, height);
      rect(wall.slateTop, x, y, width, 2);
      rect(TOWN_INK, x, y + height - 1, width, 1);
    };
    slate(0, 0, 16, WORLD_HEIGHT);
    slate(0, WORLD_HEIGHT - 16, WORLD_WIDTH, 16);
    slate(WORLD_WIDTH - TILE, 0, TILE, FRONT_DOOR.y0 * TILE);
    slate(WORLD_WIDTH - TILE, FRONT_DOOR.y1 * TILE, TILE, WORLD_HEIGHT - FRONT_DOOR.y1 * TILE);
    // The open front door: daylight from the street and two retracted glass leaves.
    rect(night ? "#2b3d6b" : "#dff6ff", WORLD_WIDTH - TILE, FRONT_DOOR.y0 * TILE, TILE, (FRONT_DOOR.y1 - FRONT_DOOR.y0) * TILE);
    rect(wall.post, WORLD_WIDTH - TILE, FRONT_DOOR.y0 * TILE - 2, TILE, 6);
    rect(wall.post, WORLD_WIDTH - TILE, FRONT_DOOR.y1 * TILE - 4, TILE, 6);
    rect(wall.pane, WORLD_WIDTH - TILE + 4, FRONT_DOOR.y0 * TILE + 4, 6, 20);
    rect(wall.pane, WORLD_WIDTH - TILE + 4, FRONT_DOOR.y1 * TILE - 24, 6, 20);
    rect(TOWN_FLOOR_SWATCHES.slabs.plaza.joint, WORLD_WIDTH - TILE - 1, FRONT_DOOR.y0 * TILE, 2, (FRONT_DOOR.y1 - FRONT_DOOR.y0) * TILE);
    this.#floor = canvas;
    return canvas;
  }
}
